/**
 * 任务详情抽屉（批次2：Modal → Drawer 重构）
 * 采用内联编辑模式，点击编辑，失焦保存
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Drawer,
  Input,
  Select,
  DatePicker,
  Message,
  Tag,
  Button,
  Divider,
  Avatar,
  Spin,
  Checkbox,
  Modal,
  Dropdown,
  Menu,
} from '@arco-design/web-react';
import {
  IconClockCircle,
  IconSearch,
  IconFilter,
  IconMore,
} from '@arco-design/web-react/icon';
import dayjs, { Dayjs } from 'dayjs';
import { createLogger } from '@/utils/logger';
import { getTaskById, updateTask } from '@/api/tasks';
import { getWorkHours, deleteWorkHour } from '@/api/work-hours';
import { getTaskOperationLogs, formatLogTime } from '@/api/task-operation-logs';
import { getProjectMembers } from '@/api/project-members';
import { getProjectStages } from '@/api/projects';
import type { Task, TaskUpdateInput } from '@/types/task';
import type { ProjectStage } from '@/types/project';
import type { WorkHour } from '@/types/work-hour';
import type { TaskOperationLog } from '@/types/task-operation-log';
import WorkHourModal from './work-hour-modal';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const logger = createLogger('TaskDetailDrawer');
type DateRangeValue = [Dayjs, Dayjs];
type EditableField = 'name' | 'description' | 'assigneeId' | 'stageId' | 'status' | 'dateRange';
type SelectOption = { label: string; value: string };

const projectMemberOptionsCache = new Map<string, SelectOption[]>();
const projectStageOptionsCache = new Map<string, SelectOption[]>();
const HOUR_SCALE = 10000;

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function isDateRangeValue(value: unknown): value is DateRangeValue {
  return Array.isArray(value) &&
    value.length === 2 &&
    dayjs.isDayjs(value[0]) &&
    dayjs.isDayjs(value[1]);
}

function toScaledHourInt(value: number | string | null | undefined): number {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) {
    return 0;
  }
  return Math.round(num * HOUR_SCALE);
}

function fromScaledHourInt(value: number): number {
  return value / HOUR_SCALE;
}

function formatHourValue(value: number | string | null | undefined): string {
  const normalized = fromScaledHourInt(toScaledHourInt(value));
  if (Object.is(normalized, -0) || normalized === 0) {
    return '0';
  }
  if (Number.isInteger(normalized)) {
    return String(normalized);
  }
  return String(normalized);
}

function calculateHoursTotals(records: WorkHour[]): { planned: number; actual: number } {
  const scaledTotals = records.reduce(
    (acc, item) => {
      acc.planned += toScaledHourInt(item.plannedHours);
      acc.actual += toScaledHourInt(item.actualHours);
      return acc;
    },
    { planned: 0, actual: 0 }
  );

  return {
    planned: fromScaledHourInt(scaledTotals.planned),
    actual: fromScaledHourInt(scaledTotals.actual),
  };
}

interface TaskDetailDrawerProps {
  visible: boolean;
  taskId: string | null;
  onClose: () => void;
  onSuccess: () => void;
  isArchived?: boolean; // 是否已归档

  // 传统模式的权限 props（商业项目使用）
  canEditInfoAll?: boolean; // 是否可以编辑所有任务的名称和描述
  canEditInfoOwn?: boolean; // 是否可以编辑本人任务的名称和描述
  canEditDate?: boolean; // 是否可以编辑任务日期
  canAssign?: boolean; // 是否可以分配/修改执行者
  canChangeStatusAll?: boolean; // 是否可以修改所有任务状态
  canChangeStatusOwn?: boolean; // 是否可以修改本人任务状态
  canAddPlannedHoursAll?: boolean; // 是否可以录入所有人的计划工时
  canAddPlannedHoursOwn?: boolean; // 是否可以录入本人的计划工时
  canEditPlannedHoursAll?: boolean; // 是否可以编辑所有人的计划工时
  canEditPlannedHoursOwn?: boolean; // 是否可以编辑本人的计划工时
  canAddActualHoursAll?: boolean; // 是否可以录入所有人的实际工时
  canAddActualHoursOwn?: boolean; // 是否可以录入本人的实际工时
  canEditActualHoursAll?: boolean; // 是否可以编辑所有人的实际工时
  canEditActualHoursOwn?: boolean; // 是否可以编辑本人的实际工时
  currentUserId?: string; // 当前用户ID

  // 项目权限模式（虚拟项目使用）
  projectPermissions?: Record<string, {
    canChangeStatusAll: boolean;
    canChangeStatusOwn: boolean;
    canEditInfoAll: boolean;
    canEditInfoOwn: boolean;
    canEditDate: boolean;
    canAssign: boolean;
    canDelete: boolean;
    canAddPlannedHoursAll: boolean;
    canAddPlannedHoursOwn: boolean;
    canEditPlannedHoursAll: boolean;
    canEditPlannedHoursOwn: boolean;
    canAddActualHoursAll: boolean;
    canAddActualHoursOwn: boolean;
    canEditActualHoursAll: boolean;
    canEditActualHoursOwn: boolean;
  }>;
}

function TaskDetailDrawer({
  visible,
  taskId,
  onClose,
  onSuccess,
  isArchived = false,
  canEditInfoAll = false,
  canEditInfoOwn = false,
  canEditDate = false,
  canAssign = false,
  canChangeStatusAll = false,
  canChangeStatusOwn = false,
  canAddPlannedHoursAll = false,
  canAddPlannedHoursOwn = false,
  canEditPlannedHoursAll = false,
  canEditPlannedHoursOwn = false,
  canAddActualHoursAll = false,
  canAddActualHoursOwn = false,
  canEditActualHoursAll = false,
  canEditActualHoursOwn = false,
  currentUserId = '',
  projectPermissions = {},
}: TaskDetailDrawerProps) {
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [workHours, setWorkHours] = useState<WorkHour[]>([]);
  const [operationLogs, setOperationLogs] = useState<TaskOperationLog[]>([]);
  const [showAllWorkHours, setShowAllWorkHours] = useState(false);
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [users, setUsers] = useState<SelectOption[]>([]);
  const [stages, setStages] = useState<SelectOption[]>([]);
  const [workHourModalVisible, setWorkHourModalVisible] = useState(false);
  const [currentHoursType, setCurrentHoursType] = useState<'planned' | 'actual'>('actual');
  const [editingWorkHour, setEditingWorkHour] = useState<WorkHour | null>(null);
  const [plannedHoursDetailVisible, setPlannedHoursDetailVisible] = useState(false);
  const isMountedRef = useRef(true);
  const workHoursTotals = useMemo(() => calculateHoursTotals(workHours), [workHours]);

  // 计算实际权限（优先使用项目权限模式）
  const actualPermissions = useMemo(() => {
    const isTaskOwnedByCurrentUser = task?.assigneeId === currentUserId;

    // 如果有项目权限且任务已加载
    if (projectPermissions && task?.projectId) {
      const projectPerms = projectPermissions[task.projectId];
      if (projectPerms) {
        return {
          canEditInfo: projectPerms.canEditInfoAll || (projectPerms.canEditInfoOwn && isTaskOwnedByCurrentUser),
          canEditDate: projectPerms.canEditDate,
          canAssign: projectPerms.canAssign,
          canChangeStatus: projectPerms.canChangeStatusAll || (projectPerms.canChangeStatusOwn && isTaskOwnedByCurrentUser),
          canAddPlannedHours: projectPerms.canAddPlannedHoursAll || (projectPerms.canAddPlannedHoursOwn && isTaskOwnedByCurrentUser),
          canEditPlannedHours: projectPerms.canEditPlannedHoursAll || (projectPerms.canEditPlannedHoursOwn && isTaskOwnedByCurrentUser),
          canAddActualHours: projectPerms.canAddActualHoursAll || (projectPerms.canAddActualHoursOwn && isTaskOwnedByCurrentUser),
          canEditActualHours: projectPerms.canEditActualHoursAll || (projectPerms.canEditActualHoursOwn && isTaskOwnedByCurrentUser),
        };
      }
    }

    // 回退到传统模式
    return {
      canEditInfo: canEditInfoAll || (canEditInfoOwn && isTaskOwnedByCurrentUser),
      canEditDate: canEditDate,
      canAssign: canAssign,
      canChangeStatus: canChangeStatusAll || (canChangeStatusOwn && isTaskOwnedByCurrentUser),
      canAddPlannedHours: canAddPlannedHoursAll || (canAddPlannedHoursOwn && isTaskOwnedByCurrentUser),
      canEditPlannedHours: canEditPlannedHoursAll || (canEditPlannedHoursOwn && isTaskOwnedByCurrentUser),
      canAddActualHours: canAddActualHoursAll || (canAddActualHoursOwn && isTaskOwnedByCurrentUser),
      canEditActualHours: canEditActualHoursAll || (canEditActualHoursOwn && isTaskOwnedByCurrentUser),
    };
  }, [
    task,
    projectPermissions,
    currentUserId,
    canEditInfoAll,
    canEditInfoOwn,
    canEditDate,
    canAssign,
    canChangeStatusAll,
    canChangeStatusOwn,
    canAddPlannedHoursAll,
    canAddPlannedHoursOwn,
    canEditPlannedHoursAll,
    canEditPlannedHoursOwn,
    canAddActualHoursAll,
    canAddActualHoursOwn,
    canEditActualHoursAll,
    canEditActualHoursOwn,
  ]);

  // 计算工时记录的编辑权限（基于工时记录所有者，而非任务所有者）
  const canEditWorkHour = useMemo(() => {
    return (workHourUserId: string, type: 'planned' | 'actual') => {
      // 如果有项目权限且任务已加载
      if (projectPermissions && task?.projectId) {
        const projectPerms = projectPermissions[task.projectId];
        if (projectPerms) {
          // 项目权限模式：基于工时记录所有者
          if (type === 'planned') {
            return projectPerms.canEditPlannedHoursAll ||
              (projectPerms.canEditPlannedHoursOwn && workHourUserId === currentUserId);
          } else {
            return projectPerms.canEditActualHoursAll ||
              (projectPerms.canEditActualHoursOwn && workHourUserId === currentUserId);
          }
        }
      }

      // 回退到传统模式：基于 props
      if (type === 'planned') {
        return canEditPlannedHoursAll || (canEditPlannedHoursOwn && workHourUserId === currentUserId);
      } else {
        return canEditActualHoursAll || (canEditActualHoursOwn && workHourUserId === currentUserId);
      }
    };
  }, [
    projectPermissions,
    task?.projectId,
    currentUserId,
    canEditPlannedHoursAll,
    canEditPlannedHoursOwn,
    canEditActualHoursAll,
    canEditActualHoursOwn,
  ]);

  // 计算项目显示文本（批次3：计件制项目显示"框架名称 / 需求名称"）
  function getProjectDisplayText(): string {
    if (!task) return '';

    if (task.projectType === '计件制') {
      const framework = task.frameworkName || task.projectName;
      const requirement = task.requirementName || '未选择需求';
      return `${framework} / ${requirement}`;
    }
    return task.projectName;
  }

  // 临时编辑值
  const [tempName, setTempName] = useState('');
  const [tempDescription, setTempDescription] = useState('');
  const [tempAssigneeId, setTempAssigneeId] = useState('');
  const [tempStageId, setTempStageId] = useState('');
  const [tempDateRange, setTempDateRange] = useState<DateRangeValue | undefined>(undefined);

  // 数据加载函数（使用 useCallback 避免不必要的重新渲染）
  const loadTaskDetail = useCallback(async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      const data = await getTaskById(taskId);
      if (isMountedRef.current) {
        setTask(data);
        setTempName(data.name);
        setTempDescription(data.description || '');
        setTempAssigneeId(data.assigneeId || '');
        setTempStageId(data.stageId || '');
        setUsers(
          data.assigneeId && data.assigneeName
            ? [{ label: data.assigneeName, value: data.assigneeId }]
            : []
        );
        setStages(
          data.stageId && data.stageName
            ? [{ label: data.stageName, value: data.stageId }]
            : []
        );
        if (data.startDate && data.endDate) {
          setTempDateRange([dayjs(data.startDate), dayjs(data.endDate)]);
        } else {
          setTempDateRange(undefined);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        Message.error('获取任务详情失败');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [taskId]);

  const loadWorkHours = useCallback(async () => {
    if (!taskId) return;
    try {
      const result = await getWorkHours({ taskId });
      if (isMountedRef.current) {
        const records = result.data || [];
        const totals = calculateHoursTotals(records);
        setWorkHours(records);
        setTask((prev) =>
          prev
            ? {
                ...prev,
                plannedHours: totals.planned,
                actualHours: totals.actual,
              }
            : prev
        );
      }
    } catch (error) {
      logger.error('获取工时记录失败:', error);
    }
  }, [taskId]);

  const loadOperationLogs = useCallback(async () => {
    if (!taskId) return;
    try {
      const result = await getTaskOperationLogs(taskId, { pageSize: 5 });
      if (isMountedRef.current) {
        setOperationLogs(result.data);
      }
    } catch (error) {
      logger.error('获取操作日志失败:', error);
    }
  }, [taskId]);

  useEffect(() => {
    isMountedRef.current = true;
    let secondaryTimer: number | null = null;

    if (visible && taskId) {
      setWorkHours([]);
      setOperationLogs([]);
      loadTaskDetail();
      // 先渲染主信息，次要区块在下一帧拉取，减轻首开阻塞感
      secondaryTimer = window.setTimeout(() => {
        void loadWorkHours();
        void loadOperationLogs();
      }, 0);
    }
    return () => {
      if (secondaryTimer !== null) {
        window.clearTimeout(secondaryTimer);
      }
      isMountedRef.current = false;
    };
  }, [visible, taskId, loadTaskDetail, loadWorkHours, loadOperationLogs]);

  const loadUsers = useCallback(async () => {
    if (!task?.projectId) return;
    const projectId = task.projectId;
    const cached = projectMemberOptionsCache.get(projectId);
    if (cached) {
      setUsers(cached);
      return;
    }

    try {
      const result = await getProjectMembers(projectId);
      if (isMountedRef.current) {
        const userOptions = (result.data?.members || []).map((member) => ({
          label: member.name,
          value: member.userId,
        }));
        projectMemberOptionsCache.set(projectId, userOptions);
        setUsers(userOptions);
      }
    } catch (error) {
      logger.error('获取项目成员失败:', error);
    }
  }, [task?.projectId]);

  const handleAssigneeDropdownVisibleChange = useCallback(
    (nextVisible: boolean) => {
      if (!nextVisible) return;
      void loadUsers();
    },
    [loadUsers]
  );

  const loadStages = useCallback(async () => {
    if (!task?.projectId) return;
    const projectId = task.projectId;
    const cached = projectStageOptionsCache.get(projectId);
    if (cached) {
      setStages(cached);
      return;
    }

    try {
      const result = await getProjectStages(projectId);
      if (isMountedRef.current) {
        const stageOptions = result.map((stage: ProjectStage) => ({
          label: stage.name,
          value: stage.id,
        }));
        projectStageOptionsCache.set(projectId, stageOptions);
        setStages(stageOptions);
      }
    } catch (error) {
      logger.error('获取项目阶段失败:', error);
    }
  }, [task?.projectId]);

  // 仅预热阶段列表；成员列表改为下拉展开时加载
  useEffect(() => {
    if (!task?.projectId) return;
    const timer = window.setTimeout(() => {
      void loadStages();
    }, 120);
    return () => {
      window.clearTimeout(timer);
    };
  }, [task?.projectId, loadStages]);

  // 保存字段修改
  async function handleFieldSave(field: EditableField, value: string | DateRangeValue | undefined) {
    if (!task) return;

    try {
      const input: TaskUpdateInput = {};

      switch (field) {
        case 'name':
          if (typeof value !== 'string' || !value || value === task.name) return;
          input.name = value;
          break;
        case 'description':
          if (typeof value !== 'string') return;
          if (value === task.description) return;
          input.description = value || undefined;
          break;
        case 'assigneeId':
          if (typeof value !== 'string' && value !== undefined) return;
          if (value === task.assigneeId) return;
          input.assigneeId = typeof value === 'string' && value ? value : undefined;
          break;
        case 'stageId':
          if (typeof value !== 'string' && value !== undefined) return;
          if (value === task.stageId) return;
          input.stageId = typeof value === 'string' && value ? value : undefined;
          break;
        case 'status':
          if (value !== 'completed' && value !== 'in_progress') return;
          if (value === task.status) return;
          input.status = value;
          break;
        case 'dateRange': {
          if (!isDateRangeValue(value)) return;
          const [start, end] = value;
          const startDate = dayjs(start).format('YYYY-MM-DD');
          const endDate = dayjs(end).format('YYYY-MM-DD');
          if (startDate === task.startDate && endDate === task.endDate) return;
          input.startDate = startDate;
          input.endDate = endDate;
          break;
        }
        default:
          return;
      }

      await updateTask(task.id, input);

      if (isMountedRef.current) {
        Message.success('更新成功');
        loadTaskDetail();
        loadOperationLogs(); // 刷新操作日志
        onSuccess();
      }
    } catch (error) {
      if (isMountedRef.current) {
        Message.error('更新失败');
      }
    } finally {
      if (isMountedRef.current) {
        setEditingField(null);
      }
    }
  }

  // 打开工时录入弹窗
  function openWorkHourModal(hoursType: 'planned' | 'actual') {
    setCurrentHoursType(hoursType);
    setEditingWorkHour(null); // 清除编辑状态
    setWorkHourModalVisible(true);
  }

  // 打开工时编辑弹窗
  function openEditWorkHourModal(workHour: WorkHour, hoursType: 'planned' | 'actual') {
    setCurrentHoursType(hoursType);
    setEditingWorkHour(workHour);
    setWorkHourModalVisible(true);
  }

  // 工时录入/编辑成功回调
  function handleWorkHourSuccess() {
    loadWorkHours();
    loadTaskDetail(); // 刷新工时汇总
    loadOperationLogs(); // 刷新操作日志
    setWorkHourModalVisible(false);
    setEditingWorkHour(null); // 清除编辑状态
  }

  // 删除工时记录（智能删除）
  async function handleDeleteWorkHour(
    workHour: WorkHour,
    workHourInfo: string,
    hoursType?: 'planned' | 'actual'
  ) {
    // 智能判断删除类型
    let deleteType: 'planned' | 'actual' | undefined = hoursType;

    if (!deleteType) {
      const hasPlanned = workHour.plannedHours > 0;
      const hasActual = workHour.actualHours > 0;

      logger.debug('智能判断删除类型:', {
        workHourId: workHour.id,
        plannedHours: workHour.plannedHours,
        actualHours: workHour.actualHours,
        hasPlanned,
        hasActual,
      });

      if (hasPlanned && !hasActual) {
        deleteType = 'planned';
      } else if (!hasPlanned && hasActual) {
        deleteType = 'actual';
      } else if (hasPlanned && hasActual) {
        // 两者都有，默认只删除计划工时，保留实际工时
        deleteType = 'planned';
      }

      logger.debug('最终删除类型:', deleteType);
    }

    const confirmMessage = deleteType === 'planned'
      ? '确定删除计划工时吗？'
      : deleteType === 'actual'
      ? '确定删除实际工时吗？'
      : `确定要删除工时记录"${workHourInfo}"吗？`;

    Modal.confirm({
      title: '确认删除',
      content: confirmMessage,
      onOk: async () => {
        try {
          logger.debug('调用 deleteWorkHour:', {
            id: workHour.id,
            deleteType,
          });

          await deleteWorkHour(workHour.id, deleteType);
          if (isMountedRef.current) {
            Message.success('删除成功');
            loadWorkHours();
            loadTaskDetail(); // 刷新工时汇总
            loadOperationLogs(); // 刷新操作日志
          }
        } catch (error: unknown) {
          if (isMountedRef.current) {
            Message.error(getErrorMessage(error, '删除失败'));
          }
        }
      },
    });
  }

  if (!task && !loading) {
    return null;
  }

  return (
    <>
      <Drawer
        width={600}
        title={null}
        visible={visible}
        onCancel={onClose}
        footer={null}
        unmountOnExit
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin />
          </div>
        ) : task ? (
          <div style={{ padding: '12px 0' }}>
            {/* 1. 任务标题区域 */}
            <div style={{ marginBottom: 24 }}>
              {editingField === 'name' ? (
                <Input
                  value={tempName}
                  onChange={setTempName}
                  onBlur={() => handleFieldSave('name', tempName)}
                  onPressEnter={() => handleFieldSave('name', tempName)}
                  autoFocus
                  style={{ fontSize: 24, fontWeight: 500 }}
                />
              ) : (
                <div
                  onClick={() => !isArchived && actualPermissions.canEditInfo && setEditingField('name')}
                  style={{
                    fontSize: 24,
                    fontWeight: 500,
                    cursor: (isArchived || !actualPermissions.canEditInfo) ? 'default' : 'pointer',
                    padding: '4px 8px',
                    borderRadius: 4,
                    border: '1px solid transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (!isArchived && actualPermissions.canEditInfo) {
                      e.currentTarget.style.border = '1px solid #e5e6eb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.border = '1px solid transparent';
                  }}
                >
                  {task.name}
                </div>
              )}
            </div>

            {/* 2. 基本信息区域 */}
            <div style={{ marginBottom: 24 }}>
              {/* 状态 */}
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#86909c', width: 80 }}>状态：</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Checkbox
                    checked={task.status === 'completed'}
                    onChange={(checked) =>
                      handleFieldSave('status', checked ? 'completed' : 'in_progress')
                    }
                    disabled={isArchived || !actualPermissions.canChangeStatus}
                  />
                  <Tag
                    color={task.status === 'completed' ? 'green' : 'blue'}
                    size="medium"
                  >
                    {task.status === 'completed' ? '已完成' : '进行中'}
                  </Tag>
                </div>
              </div>

              {/* 执行者 */}
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#86909c', width: 80 }}>执行者：</span>
                <Select
                  value={tempAssigneeId}
                  onChange={(value) => {
                    const nextValue = typeof value === 'string' ? value : '';
                    setTempAssigneeId(nextValue);
                    handleFieldSave('assigneeId', nextValue || undefined);
                  }}
                  onVisibleChange={handleAssigneeDropdownVisibleChange}
                  options={users}
                  placeholder="选择执行者"
                  allowClear
                  style={{ flex: 1 }}
                  disabled={isArchived || !actualPermissions.canAssign}
                />
              </div>

              {/* 时间 */}
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#86909c', width: 80 }}>时间：</span>
                <RangePicker
                  value={tempDateRange}
                  onChange={(_dateStrings, dates) => {
                    const nextRange = dates && dates[0] && dates[1]
                      ? [dates[0], dates[1]] as DateRangeValue
                      : undefined;
                    setTempDateRange(nextRange);
                    // 日期选择完成后自动保存
                    if (nextRange) {
                      handleFieldSave('dateRange', nextRange);
                    }
                  }}
                  style={{ flex: 1 }}
                  disabled={isArchived || !actualPermissions.canEditDate}
                />
              </div>

              {/* 项目 */}
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#86909c', width: 80 }}>项目：</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                  <span style={{ color: '#86909c' }}>{getProjectDisplayText()}</span>
                  {stages.length > 0 && (
                    <>
                      <span style={{ color: '#86909c' }}>/</span>
                      <Select
                        value={tempStageId}
                        onChange={(value) => {
                          setTempStageId(value);
                          handleFieldSave('stageId', value);
                        }}
                        options={stages}
                        placeholder="请选择阶段"
                        allowClear
                        style={{ flex: 1 }}
                        disabled={isArchived}
                      />
                    </>
                  )}
                </div>
              </div>

              {/* 关联信息（售前支持、专业贡献、其他任务） */}
              {task.entityType === 'presale' && task.entityName && (
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#86909c', width: 80 }}>关联商机：</span>
                  <span style={{ color: '#86909c' }}>{task.entityName}</span>
                </div>
              )}
              {task.entityType === 'contribution' && task.entityName && (
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#86909c', width: 80 }}>专业贡献：</span>
                  <span style={{ color: '#86909c' }}>{task.entityName}</span>
                </div>
              )}
              {task.entityType === 'other' && task.taskType && (
                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#86909c', width: 80 }}>任务类型：</span>
                  <span style={{ color: '#86909c' }}>{task.taskType}</span>
                </div>
              )}

              {/* 备注 */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: '#86909c', marginBottom: 4 }}>备注：</div>
                {editingField === 'description' ? (
                  <TextArea
                    value={tempDescription}
                    onChange={setTempDescription}
                    onBlur={() => handleFieldSave('description', tempDescription)}
                    autoFocus
                    autoSize={{ minRows: 3, maxRows: 6 }}
                  />
                ) : (
                  <div
                    onClick={() => !isArchived && actualPermissions.canEditInfo && setEditingField('description')}
                    style={{
                      minHeight: 60,
                      padding: '4px 8px',
                      cursor: (isArchived || !actualPermissions.canEditInfo) ? 'default' : 'pointer',
                      borderRadius: 4,
                      border: '1px solid transparent',
                      color: task.description ? '#1d2129' : '#86909c',
                    }}
                    onMouseEnter={(e) => {
                      if (!isArchived && actualPermissions.canEditInfo) {
                        e.currentTarget.style.border = '1px solid #e5e6eb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.border = '1px solid transparent';
                    }}
                  >
                    {task.description || '点击添加备注...'}
                  </div>
                )}
              </div>
            </div>

            <Divider />

            {/* 3. 工时区域 */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>
                工时
              </div>

              {/* 计划工时行 */}
              <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IconClockCircle />
                  <span>计划工时</span>
                  <div
                    style={{
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: '4px 16px',
                      borderRadius: 4,
                      transition: 'background-color 0.2s',
                      minWidth: '80px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e8e8e8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    onClick={() => setPlannedHoursDetailVisible(true)}
                  >
                    {formatHourValue(
                      workHours.length > 0 ? workHoursTotals.planned : (task.plannedHours || 0)
                    )}{' '}
                    小时
                  </div>
                </div>
                {!isArchived && actualPermissions.canAddPlannedHours && (
                  <Button size="small" type="text" onClick={() => openWorkHourModal('planned')}>
                    + 计划工时
                  </Button>
                )}
              </div>

              {/* 实际工时行 */}
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <IconClockCircle />
                  <span>实际工时</span>
                  <div style={{ fontWeight: 500, padding: '4px 16px', minWidth: '80px' }}>
                    {formatHourValue(
                      workHours.length > 0 ? workHoursTotals.actual : (task.actualHours || 0)
                    )}{' '}
                    小时
                  </div>
                </div>
                {!isArchived && actualPermissions.canAddActualHours && (
                  <Button size="small" type="text" onClick={() => openWorkHourModal('actual')}>
                    + 实际工时
                  </Button>
                )}
              </div>

              {/* 实际工时记录列表 */}
              {workHours.filter(h => h.actualHours > 0).length > 0 && (
                <div style={{ background: '#f7f8fa', padding: 12, borderRadius: 4 }}>
                  {workHours
                    .filter(h => h.actualHours > 0)
                    .slice(0, showAllWorkHours ? undefined : 5)
                    .map((h) => (
                      <div
                        key={h.id}
                        style={{
                          marginBottom: 12,
                          padding: '8px',
                          borderRadius: 4,
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e8e8e8';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4e5969' }}>
                            <Avatar size={20}>{h.userName[0]}</Avatar>
                            <span>
                              {h.userName} {dayjs(h.workDate).format('M月D日')}实际工时为{' '}
                              {formatHourValue(h.actualHours)} 小时
                            </span>
                          </div>
                          {!isArchived && canEditWorkHour(h.userId, 'actual') && (
                            <Dropdown
                              droplist={
                                <Menu
                                  onClickMenuItem={(key) => {
                                    if (key === 'edit') {
                                      openEditWorkHourModal(h, 'actual');
                                    } else if (key === 'delete') {
                                      const info = `${h.userName} ${dayjs(h.workDate).format('M月D日')}实际工时 ${formatHourValue(h.actualHours)} 小时`;
                                      handleDeleteWorkHour(h, info, 'actual');
                                    }
                                  }}
                                >
                                  <Menu.Item key="edit">编辑</Menu.Item>
                                  <Menu.Item key="delete">删除</Menu.Item>
                                </Menu>
                              }
                              trigger="hover"
                            >
                              <IconMore style={{ cursor: 'pointer', color: '#86909c' }} />
                            </Dropdown>
                          )}
                        </div>
                        {h.description && (
                          <div
                            style={{
                              marginLeft: 28,
                              marginTop: 4,
                              padding: '4px 8px',
                              background: '#ffffff',
                              borderRadius: 4,
                              fontSize: 12,
                              color: '#4e5969',
                            }}
                          >
                            {h.description}
                          </div>
                        )}
                      </div>
                    ))}
                  {workHours.filter(h => h.actualHours > 0).length > 5 && !showAllWorkHours && (
                    <div
                      style={{ textAlign: 'center', color: '#165dff', cursor: 'pointer' }}
                      onClick={() => setShowAllWorkHours(true)}
                    >
                      ··· 加载更多
                    </div>
                  )}
                </div>
              )}
            </div>

            <Divider />

            {/* 4. 任务记录区域（操作日志） */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 500 }}>任务记录</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <IconSearch style={{ cursor: 'pointer', color: '#86909c' }} />
                  <IconFilter style={{ cursor: 'pointer', color: '#86909c' }} />
                </div>
              </div>

              {operationLogs.length > 5 && !showAllLogs && (
                <div
                  style={{ textAlign: 'center', color: '#165dff', cursor: 'pointer', marginBottom: 8 }}
                  onClick={() => setShowAllLogs(true)}
                >
                  ··· 显示较早的 {operationLogs.length - 5} 条动态
                </div>
              )}

              {operationLogs.slice(0, showAllLogs ? undefined : 5).map((log) => (
                <div key={log.id} style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <IconClockCircle style={{ marginTop: 2, color: '#86909c' }} />
                  <div style={{ flex: 1, fontSize: 13, color: '#4e5969' }}>
                    {log.displayText}
                  </div>
                  <div style={{ fontSize: 12, color: '#86909c', whiteSpace: 'nowrap' }}>
                    {formatLogTime(log.createdAt)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Drawer>

      {/* 工时录入弹窗 */}
      {task && (
        <WorkHourModal
          visible={workHourModalVisible}
          hoursType={currentHoursType}
          taskId={task.id}
          taskName={task.name}
          assigneeId={task.assigneeId}
          assigneeName={task.assigneeName}
          editWorkHour={editingWorkHour}
          onClose={() => {
            setWorkHourModalVisible(false);
            setEditingWorkHour(null);
          }}
          onSuccess={handleWorkHourSuccess}
        />
      )}

      {/* 计划工时明细弹窗 */}
      <Modal
        title="计划工时明细"
        visible={plannedHoursDetailVisible}
        onCancel={() => setPlannedHoursDetailVisible(false)}
        footer={null}
        style={{ width: 600 }}
      >
        <div style={{ maxHeight: 500, overflowY: 'auto' }}>
          {workHours.filter(h => h.plannedHours > 0).length > 0 ? (
            <div>
              {workHours
                .filter(h => h.plannedHours > 0)
                .map((h) => (
                  <div
                    key={h.id}
                    style={{
                      marginBottom: 12,
                      padding: '12px',
                      borderRadius: 4,
                      background: '#f7f8fa',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e8e8e8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f7f8fa';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4e5969' }}>
                        <Avatar size={20}>{h.userName[0]}</Avatar>
                        <span>
                          {h.userName} {dayjs(h.workDate).format('M月D日')}计划工时为{' '}
                          {formatHourValue(h.plannedHours)} 小时
                        </span>
                      </div>
                      {!isArchived && canEditWorkHour(h.userId, 'planned') && (
                        <Dropdown
                          droplist={
                            <Menu
                              onClickMenuItem={(key) => {
                                if (key === 'edit') {
                                  openEditWorkHourModal(h, 'planned');
                                } else if (key === 'delete') {
                                  const info = `${h.userName} ${dayjs(h.workDate).format('M月D日')}计划工时 ${formatHourValue(h.plannedHours)} 小时`;
                                  handleDeleteWorkHour(h, info, 'planned');
                                }
                              }}
                            >
                              <Menu.Item key="edit">编辑</Menu.Item>
                              <Menu.Item key="delete">删除</Menu.Item>
                            </Menu>
                          }
                          trigger="hover"
                        >
                          <IconMore style={{ cursor: 'pointer', color: '#86909c' }} />
                        </Dropdown>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#86909c' }}>
              暂无计划工时记录
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

export default TaskDetailDrawer;
